import React, { useEffect } from "react"
import { Box, Checkbox, FormControlLabel, Grid, Typography } from "@mui/material"
import { AppPillToggle } from "@components"
import { dsColorNew } from '@palette'
import { NumberFieldSeeker } from "@ui-seeker/core"
import { dsRepricingDrawerData, dsRepricingDrawerErrors } from "Pages/DsRepricing/Page/@signals"

export const STRATEGY = { MARKET_TREND: 'market_trend', RANKING: 'ranking' }

// Market Trend averages the competitors in scope; Ranking targets a position. Each one owns a
// disjoint set of sub-fields, so switching strategy clears the other one's values.
const emptyStrategy = type => type === STRATEGY.RANKING
	? { type, targetPosition: null, keepPosition: false, keepPositionMargin: null }
	: { type, rankCap: null, excludeOutliersPct: null }

const isValidStrategy = s => {
	if (!s?.type) return false
	if (s.type === STRATEGY.RANKING) {
		return Number.isInteger(Number(s.targetPosition)) && Number(s.targetPosition) >= 1 && Number(s.targetPosition) <= 20
	}
	const rankCapOk = s.rankCap == null || (Number.isInteger(Number(s.rankCap)) && Number(s.rankCap) >= 1 && Number(s.rankCap) <= 20)
	const outliersOk = s.excludeOutliersPct != null && s.excludeOutliersPct !== '' && Number(s.excludeOutliersPct) > 0 && Number(s.excludeOutliersPct) <= 500
	return rankCapOk && outliersOk
}

export default function DsRepricingStrategy() {
	const strategy = dsRepricingDrawerData.value.strategy ?? emptyStrategy(STRATEGY.MARKET_TREND)
	const isRanking = strategy.type === STRATEGY.RANKING

	useEffect(() => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.value, strategy: isValidStrategy(strategy) }
	}, [JSON.stringify(strategy)])

	const setStrategy = next => {
		dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, strategy: next }
	}

	const handleType = (_, newType) => {
		if (newType === null || newType === strategy.type) return
		setStrategy(emptyStrategy(newType))
	}

	const handleField = (key, raw) => {
		setStrategy({ ...strategy, [key]: raw !== '' ? Number(raw) : null })
	}

	// The guard is meaningless in first place: there is no competitor above the top of the list to
	// be pushed back behind, so turning it on there would be a switch that does nothing.
	const guardApplies = isRanking && Number(strategy.targetPosition) >= 2

	const targetPositionError = isRanking && (strategy.targetPosition == null || strategy.targetPosition === '')
		? "Required for Ranking"
		: isRanking && !(Number(strategy.targetPosition) >= 1 && Number(strategy.targetPosition) <= 20)
			? "Must be between 1 and 20"
			: undefined

	return (
		<Box>
			<Typography sx={{ fontSize: 14, color: dsColorNew.textDark, mb: 1, fontWeight: 600 }}>Strategy</Typography>
			<AppPillToggle size="small" value={strategy.type} onChange={value => handleType(null, value)} sx={{ mb: 2 }}
			               options={[
				               { value: STRATEGY.MARKET_TREND, label: 'Market Trend' },
				               { value: STRATEGY.RANKING, label: 'Ranking' },
			               ]}/>

			{isRanking ? (
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<NumberFieldSeeker
							label="Target Position"
							placeholder="1"
							value={strategy.targetPosition ?? ''}
							onChange={e => handleField('targetPosition', e.target.value)}
							error={targetPositionError}
						/>
					</Grid>
					<Grid item xs={12}>
						<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
							The offset below is applied against the competitor currently holding that position —
							undercut them and you take the slot, pushing them one place down.
						</Typography>
					</Grid>
					{guardApplies && (
						<>
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={!!strategy.keepPosition}
											onChange={e => setStrategy({ ...strategy, keepPosition: e.target.checked })}
										/>
									}
									label={
										<Typography sx={{ fontSize: 14, color: dsColorNew.textDark }}>
											Never take a better position than the target
										</Typography>
									}
								/>
							</Grid>
							{strategy.keepPosition && (
								<>
									<Grid item xs={6}>
										<NumberFieldSeeker
											label="Safety margin (€)"
											placeholder="0.01"
											value={strategy.keepPositionMargin ?? ''}
											onChange={e => handleField('keepPositionMargin', e.target.value)}
											error={
												strategy.keepPositionMargin != null && strategy.keepPositionMargin !== '' &&
												Number(strategy.keepPositionMargin) < 0 ? "Cannot be negative" : undefined
											}
										/>
									</Grid>
									<Grid item xs={12}>
										<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
											A wide offset can overshoot the slot: 10% off a competitor at 110 is 99, which is
											first and not second. With this on, the bid is pushed back to this much above the
											competitor in front instead. Empty means one cent — just enough to clear them.
										</Typography>
									</Grid>
								</>
							)}
						</>
					)}
				</Grid>
			) : (
				<Grid container spacing={2}>
					<Grid item xs={6}>
						<NumberFieldSeeker
							label="Rank cap (optional)"
							placeholder="No cap"
							value={strategy.rankCap ?? ''}
							onChange={e => handleField('rankCap', e.target.value)}
							error={
								strategy.rankCap != null && strategy.rankCap !== '' &&
								!(Number(strategy.rankCap) >= 1 && Number(strategy.rankCap) <= 20)
									? "Must be between 1 and 20"
									: undefined
							}
						/>
					</Grid>
					<Grid item xs={6}>
						<NumberFieldSeeker
							label="Exclude outliers over (%)"
							placeholder="e.g. 50"
							startIcon="percentage"
							value={strategy.excludeOutliersPct ?? ''}
							onChange={e => handleField('excludeOutliersPct', e.target.value)}
							error={
								strategy.excludeOutliersPct == null || strategy.excludeOutliersPct === ''
									? "Required"
									: !(Number(strategy.excludeOutliersPct) > 0 && Number(strategy.excludeOutliersPct) <= 500)
										? "Must be between 0 and 500"
										: undefined
							}
						/>
					</Grid>
					<Grid item xs={12}>
						<Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
							Rank cap keeps the bid from taking a <b>better</b> position than that one — a cap of 2
							means never being the cheapest. Outlier exclusion ignores competitors priced further than
							the given percentage from the middle of the market, in either direction: the one who
							dropped their price to break it, and the one out of fleet asking a silly number. The
							average is then taken over whoever is left.
						</Typography>
					</Grid>
				</Grid>
			)}
		</Box>
	)
}